// Owner's decision 47, fix 2 (BD-172): the reader's dates in any common form ("27 Oct 2026", "27/10/2026",
// "2026/10/27") are converted to YYYY-MM-DD before the read is checked, so one date never stops a whole read. When day
// and month could be swapped (05/06/2026) and no other date on the document settles the order, the field is named as
// ambiguous so the read asks instead of guessing; South African documents write day first, which is the value kept.
// Decision 80 #3 (findings addendum, item 1): the South African forms too: Afrikaans month names ("15 Maart 2025",
// "1 Februarie 2025"), d-Mon-yyyy ("8-Oct-2025", "8-Okt-2025"), ordinals ("8th of October 2025") and a trailing time
// ("2025-10-08T00:00:00", "15/05/2025 08:00"), which is dropped. A partial or impossible date is still refused.

export type DateOrder = "dmy" | "mdy";

// Decision 80 #3 round 3: a month is a month name in full or its exact short form, English or Afrikaans; never a word
// that only starts like one ("8 Mayhem 2025" is not 8 May).
const MONTHS: Record<string, number> = Object.fromEntries(([
  ["jan", "january", "januarie"], ["feb", "february", "februarie"], ["mar", "mrt", "march", "maart"], ["apr", "april"], ["may", "mei"],
  ["jun", "june", "junie"], ["jul", "july", "julie"], ["aug", "august", "augustus"], ["sep", "sept", "september"],
  ["oct", "okt", "october", "oktober"], ["nov", "november"], ["dec", "des", "december", "desember"]
] as const).flatMap((names, i) => names.map((n) => [n, i + 1])));

const pad = (n: number) => String(n).padStart(2, "0");
const year = (y: string) => (y.length === 2 ? 2000 + Number(y) : Number(y));
function iso(y: number, m: number, d: number): string | null {
  if (!(m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100)) return null;
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCMonth() === m - 1 && t.getUTCDate() === d ? `${y}-${pad(m)}-${pad(d)}` : null;
}
const month = (word: string) => MONTHS[word.toLowerCase()];

/**
 * The date part of a value printed with a time after it ("2025-10-08T00:00:00", "15/05/2025 08:00", "8 Oct 2025 10:30",
 * "2025-10-08 14:05:00Z"). Null when the time itself is not a time of day ("2025-10-08T25:00"): the date is refused.
 */
function withoutTime(s: string): string | null {
  const m = /^(.*\d)(?:T|\s+)(\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(?:(Z|[+-]\d{2}:?\d{2})|([ap])\.?m\.?)?$/i.exec(s);
  if (!m) return s;
  const h = Number(m[2]), min = Number(m[3]), sec = Number(m[4] ?? 0);
  // Round 3: with am or pm the hour is on a 12-hour clock ("1:30 pm"); "13:30 pm" is not a time, so the date is refused.
  const hourOk = m[6] ? h >= 1 && h <= 12 : h <= 23;
  return hourOk && min <= 59 && sec <= 59 ? m[1]!.trim() : null;
}

/** One date in a common form, as YYYY-MM-DD; ambiguous when day and month could be swapped and the order is not known. */
export function normaliseDate(raw: string, order?: DateOrder): { iso: string | null; ambiguous: boolean } {
  const s = withoutTime(String(raw ?? "").trim().replace(/^[a-z]+day,?\s+/i, ""));
  if (s === null) return { iso: null, ambiguous: false };
  let m: RegExpExecArray | null;
  // Year first: 2026-10-27, 2026/10/27, 2026.10.27.
  if ((m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(s))) return { iso: iso(Number(m[1]), Number(m[2]), Number(m[3])), ambiguous: false };
  // A month in words: 27 Oct 2026, 27 October 2026, 15 Maart 2025, 8th of October 2025, 8-Oct-2025, October 27, 2026.
  if ((m = /^(\d{1,2})(?:st|nd|rd|th)?(?:\s+of)?\s+([a-z]+)\.?,?\s+(\d{2,4})$/i.exec(s))
      || (m = /^(\d{1,2})[-/.]([a-z]+)\.?[-/.](\d{2,4})$/i.exec(s))) {
    const mo = month(m[2]!);
    return { iso: mo ? iso(year(m[3]!), mo, Number(m[1])) : null, ambiguous: false };
  }
  if ((m = /^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{2,4})$/i.exec(s))) {
    const mo = month(m[1]!);
    return { iso: mo ? iso(year(m[3]!), mo, Number(m[2])) : null, ambiguous: false };
  }
  // Numbers only: 27/10/2026, 27-10-2026, 27.10.2026, 27/10/26.
  if ((m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(s))) {
    const a = Number(m[1]), b = Number(m[2]), y = year(m[3]!);
    if (a > 12 || order === "dmy") return { iso: iso(y, b, a), ambiguous: false };
    if (b > 12 || order === "mdy") return { iso: iso(y, a, b), ambiguous: false };
    return { iso: iso(y, b, a), ambiguous: a !== b };
  }
  return { iso: null, ambiguous: false };
}

const DATE_FIELD = /(^|_)date$|^date$|_at_date$/;

/**
 * Every date field of a read (any key ending in "date", nested ones too) converted to YYYY-MM-DD. The order of day and
 * month is taken from the document itself when any of its numeric dates settles it (a part above 12), else from
 * `order`. Returns the fields that stay ambiguous and the ones that are not dates at all (left as they were).
 */
export function normaliseExtractionDates(x: unknown, order?: DateOrder) {
  const ambiguous: { field: string; raw: string; as_day_month: string; as_month_day: string }[] = [];
  const unreadable: { field: string; raw: string }[] = [];
  const numeric: string[] = [];
  const collect = (v: unknown, key = ""): void => {
    if (Array.isArray(v)) v.forEach((e) => collect(e, key));
    else if (v && typeof v === "object") for (const [k, e] of Object.entries(v)) collect(e, k);
    else if (typeof v === "string" && DATE_FIELD.test(key)) numeric.push(v.trim());
  };
  collect(x);
  let found: DateOrder | undefined = order;
  if (!found) for (const d of numeric) {
    const m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(withoutTime(d) ?? "");
    if (m && Number(m[1]) > 12) { found = "dmy"; break; }
    if (m && Number(m[2]) > 12) { found = "mdy"; break; }
  }
  const walk = (v: unknown, path: string, key: string): unknown => {
    if (Array.isArray(v)) return v.map((e, i) => walk(e, `${path}.${i}`, key));
    if (v && typeof v === "object") return Object.fromEntries(Object.entries(v).map(([k, e]) => [k, walk(e, path ? `${path}.${k}` : k, k)]));
    if (typeof v !== "string" || !DATE_FIELD.test(key)) return v;
    // Decision 80 #3: a date that is not printed stays blank (spaces alone are blank too); it is never given a default.
    if (v.trim() === "") return "";
    const r = normaliseDate(v, found);
    if (!r.iso) { unreadable.push({ field: path, raw: v }); return v; }
    if (r.ambiguous) {
      const other = normaliseDate(v, "mdy").iso!;
      ambiguous.push({ field: path, raw: v, as_day_month: r.iso, as_month_day: other });
    }
    return r.iso;
  };
  return { extraction: walk(x, "", ""), ambiguous, unreadable, order: found ?? null };
}
